import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { useSession } from "../lib/session";
import { getRound, listEntries, openRound, reopenCard, renameRound,
         deleteRound, clearCard, removeFromRound, getTeeSheet } from "../lib/api";
import { PageHead, Loading, Notice, Empty } from "../components/ui";
import Back from "../components/Back";

export default function Round() {
  const { id: cid, roundId: id } = useParams();   // cid = competition, id = round
  const nav = useNavigate();
  const { isOrganiser, profile } = useSession();

  const [round, setRound] = useState(null);
  const [entries, setEntries] = useState([]);

  const [error, setError] = useState("");
  const [note, setNote] = useState("");
  const [sheet, setSheet] = useState(undefined);   // undefined = still looking
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const [r, e] = await Promise.all([getRound(id), listEntries(id)]);
      setRound(r); setEntries(e);
      getTeeSheet(id).then(setSheet).catch(() => setSheet(null));
    } catch (err) { setError(err.message); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  if (error && !round) return <Notice kind="error">{error}</Notice>;
  if (!round && !error) return <Loading />;
  if (!round) return (
    <>
      <Back to="/competitions">Back</Back>
      <Notice kind="error">That round isn't available.</Notice>
    </>
  );

  const open = ["scoring", "in_play"].includes(round.status);
  const mine = entries.find((e) => e.profile_id === profile?.id);
  const inCount = entries.filter((e) => ["submitted", "no_return"].includes(e.card_status)).length;

  /**
   * The database refuses when cards are in and says how many. That
   * message is worth showing rather than swallowing — then the second
   * confirmation is an informed one.
   */
  async function doDelete() {
    setError(""); setBusy(true);
    try {
      await deleteRound(id, false);
      nav(`/c/${cid}/rounds`);
    } catch (e) {
      const msg = e.message ?? String(e);
      if (/already entered/i.test(msg)) {
        setBusy(false);
        if (!window.confirm(`${msg}\n\nDelete it anyway?`)) return;
        setBusy(true);
        try {
          await deleteRound(id, true);
          nav(`/c/${cid}/rounds`);
          return;
        } catch (e2) { setError(e2.message); }
      } else { setError(msg); }
      setBusy(false);
    }
  }

  /**
   * Two destructive actions on one card, kept apart because they mean
   * different things: a card entered wrongly gets cleared and stays on
   * the sheet; a player who shouldn't be in the round comes off it.
   */
  async function wipe(entry) {
    const who = entry.profiles?.full_name ?? "this player";
    if (!window.confirm(
      `Clear ${who}'s card?\n\nEvery hole is deleted and the card goes back to ` +
      `not started. They stay on the sheet.`)) return;
    setError(""); setBusy(true);
    try {
      const n = await clearCard(entry.id);
      setNote(`${who}'s card cleared — ${n} hole${n === 1 ? "" : "s"} removed.`);
      await load();
    } catch (e) { setError(e.message); }
    finally { setBusy(false); }
  }

  async function drop(entry) {
    const who = entry.profiles?.full_name ?? "this player";
    const played = ["submitted", "no_return"].includes(entry.card_status);
    if (!window.confirm(
      `Remove ${who} from this round?` +
      (played ? "\n\nTheir card and all its scores are deleted." : "") +
      `\n\nThey stay in the competition and can be added back.`)) return;
    setError(""); setBusy(true);
    try {
      setNote(await removeFromRound(entry.id));
      await load();
    } catch (e) { setError(e.message); }
    finally { setBusy(false); }
  }

  async function doOpen() {
    setBusy(true);
    try { await openRound(id); await load(); }
    catch (e) { setError(e.message); }
    finally { setBusy(false); }
  }

  return (
    <>

      <Back to={`/c/${cid}/rounds`}>All rounds</Back>
      <Notice kind="ok">{note}</Notice>
      <PageHead title={`${round.name} · ${round.courses?.name}`}
                sub={`${round.played_on ?? "no date"} · ${entries.length} on the sheet · ${inCount} cards in`}>
        {isOrganiser && (
          <button className="btn btn-ghost" onClick={async () => {
            const next = window.prompt("Name for this round", round.name);
            if (!next || next.trim() === round.name) return;
            try { await renameRound(round.id, next.trim()); await load(); }
            catch (e) { setError(e.message); }
          }}>Rename</button>
        )}
        {isOrganiser && !open && (
          <button className="btn btn-primary" onClick={doOpen} disabled={busy}>
            {busy ? "Opening…" : "Open for scoring"}
          </button>
        )}
        {isOrganiser && (
          <button className="btn-danger" onClick={doDelete} disabled={busy}>
            Delete round
          </button>
        )}
      </PageHead>

      <Notice kind="error">{error}</Notice>

      {!open && (
        <div className="notice notice-info" style={{ marginBottom: "1rem" }}>
          This round isn't open yet. Opening it puts everyone entered onto
          their usual tees, works out each playing handicap from the index
          frozen at entry, and lets cards be entered.
        </div>
      )}

      {open && mine && !["submitted", "no_return"].includes(mine.card_status) && (
        <Link className="btn btn-primary btn-block" to={`/cards/${mine.id}`}
              style={{ textDecoration: "none", textAlign: "center", marginBottom: "1rem" }}>
          {mine.card_status === "in_progress" ? "Carry on with my card" : "Enter my card"}
        </Link>
      )}

      <Link className="btn btn-ghost btn-block"
            to={`/c/${cid}/board`}
            style={{ textDecoration: "none", textAlign: "center", marginBottom: "1rem" }}>
        Leaderboard
      </Link>

      {entries.length === 0 ? (
        <Empty title="Nobody on the sheet">
          Open the round to put everyone entered in the competition onto a card.
        </Empty>
      ) : (
        <div className="list" style={{ "--cols": "minmax(0,1fr) 4rem auto" }}>
          {entries
            .sort((a, b) => (a.profiles?.full_name ?? "").localeCompare(b.profiles?.full_name ?? ""))
            .map((e) => {
              const isIn = ["submitted", "no_return"].includes(e.card_status);
              return (
                <div className="list-row" key={e.id}>
                  <span style={{ minWidth: 0 }}>
                    <strong style={{ display: "block", overflow: "hidden",
                                     textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                      {e.profiles?.full_name}
                    </strong>
                    <small className="muted" style={{ fontSize: ".6875rem" }}>
                      {e.tee_sets?.name} · off {e.playing_handicap}
                    </small>
                  </span>
                  <span className={`pill ${isIn ? "pill-mark" : "pill-quiet"}`}>
                    {e.card_status === "no_return" ? "NR"
                      : e.card_status === "submitted" ? "in"
                      : e.card_status === "in_progress" ? "started" : "—"}
                  </span>
                  <span className="card-acts">
                    {open && !isIn && (isOrganiser || e.profile_id === profile?.id) && (
                      <Link className="btn-link" to={`/cards/${e.id}`}>Enter</Link>
                    )}
                    {open && isIn && isOrganiser && (
                      <button className="btn-link"
                              onClick={async () => {
                                try { await reopenCard(e.id); await load(); }
                                catch (err) { setError(err.message); }
                              }}>Reopen</button>
                    )}
                    {isOrganiser && (
                      <details className="rowmenu">
                        <summary aria-label="More for this card">⋯</summary>
                        <div className="rowmenu-list">
                          <Link to={`/cards/${e.id}`}>Open the card</Link>
                          <button onClick={() => wipe(e)} disabled={busy}>
                            Clear the scores
                          </button>
                          <button className="danger" onClick={() => drop(e)} disabled={busy}>
                            Remove from this round
                          </button>
                        </div>
                      </details>
                    )}
                  </span>
                </div>
              );
            })}
        </div>
      )}
    </>
  );
}
